import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Typography, Box } from '@mui/joy';
import {
    People,
    DeviceHub,
    Memory,
    AdminPanelSettings,
    Book,
    Build,
    Code,
    VideoLibrary,
} from '@mui/icons-material';
import PermissionWrapper from '../PermissionWrapper';
import BranchCard from './BranchCard';
import { TOOL_URLS, openTool } from '../../config/tools';

const NavigationBranches = () => {
    const navigate = useNavigate();

    const branches = [
        {
            id: 'clients',
            title: 'Client Management',
            description: 'Manage client accounts, view profiles and applications',
            icon: People,
            color: 'success',
            permissions: ['client:read', 'client:write'],
            onClick: () => navigate('/admin/clients'),
        },
        {
            id: 'devices',
            title: 'Device Management',
            description: 'Monitor and manage connected devices, firmware updates',
            icon: DeviceHub,
            color: 'warning',
            permissions: ['device:read', 'device:write'],
            onClick: () => navigate('/admin/devices'),
        },
        {
            id: 'firmware',
            title: 'Firmware Management',
            description: 'Manage firmware releases, approvals and deployments',
            icon: Memory,
            color: 'danger',
            permissions: ['firmware:read', 'firmware:write'],
            onClick: () => navigate('/firmware'),
        },
        {
            id: 'admins',
            title: 'Admins & Permissions',
            description: 'Manage administrator accounts and access permissions',
            icon: AdminPanelSettings,
            color: 'primary',
            permissions: [
                'dashboard:read',
                'system:read',
                'client:read', 'client:write',
                'device:read', 'device:write',
                'firmware:read',
                'admin:read', 'admin:write',
            ],
            onClick: () => navigate('/admin/permissions'),
        },
        {
            // Visible to every signed-in admin (no PermissionWrapper).
            id: 'tools',
            title: 'Tools',
            description: 'Open monitoring, collector, verification and other operator tools',
            icon: Build,
            color: 'primary',
            onClick: () => navigate('/admin/tools'),
            publicToAdmins: true,
        },
        {
            id: 'admin',
            title: 'Knowledge Base',
            description: 'Find answers to common questions and troubleshoot issues',
            icon: Book,
            color: 'neutral',
            permissions: ['admin:read', 'admin:write'],
            onClick: () => navigate('/admin/admins'),
            badge: 'Coming Soon',
            disabled: true,
        },
        {
            // Footage owners consented to upload to the verification tool (developer scope).
            id: 'footage-shares',
            title: 'Footage Shares',
            description: 'Owner-consented uploads of hub recordings to the verification tool',
            icon: VideoLibrary,
            color: 'primary',
            permissions: ['developer:read', 'developer:write'],
            onClick: () => navigate('/admin/footage-shares'),
        },
        {
            // Opens the Developer Portal (API docs) in a new tab; the portal
            // signs the admin in with Google and checks developer access itself.
            id: 'developer-portal',
            title: 'Developer Portal',
            description: 'Browse and try the Client, Device, Admin and internal API docs',
            icon: Code,
            color: 'primary',
            permissions: ['developer:read', 'developer:write'],
            onClick: () => openTool(TOOL_URLS.developerPortal),
            disabled: !TOOL_URLS.developerPortal,
        },
    ];

    return (
        <Box>
            <Typography level="h4" sx={{ mb: 3 }}>
                Administration Modules
            </Typography>
            <Grid container spacing={4} sx={{ alignItems: 'stretch' }}>
                {branches.map((branch) => {
                    const card = (
                        <Box sx={{ width: '100%', display: 'flex' }}>
                            <BranchCard
                                title={branch.title}
                                description={branch.description}
                                icon={branch.icon}
                                color={branch.color}
                                onClick={branch.onClick}
                                badge={branch.badge}
                                disabled={branch.disabled}
                            />
                        </Box>
                    );

                    return (
                        <Grid key={branch.id} xs={12} sm={6} lg={4} sx={{ display: 'flex' }}>
                            {branch.publicToAdmins ? (
                                card
                            ) : (
                                <PermissionWrapper
                                    requiredPermissions={branch.permissions}
                                    fallback={null}
                                >
                                    {card}
                                </PermissionWrapper>
                            )}
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
};

export default NavigationBranches;
